import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Save, X, Calculator, Trash2, ChevronDown, ChevronRight, Plus, Target } from 'lucide-react';
import { 
  getCurrencies,
  calculateDistribution,
  getCapitalIncreases,
  createCapitalIncrease,
  deleteCapitalIncrease,
  getInvestmentProducts
} from '../services/investmentService';

interface DistributionItem {
  product_id: number;
  product_name: string;
  area_name: string;
  platform_id: number;
  platform_name: string;
  currency_code: string;
  target_percentage: number;
  current_value_eur: number;
  target_value_eur: number;
  needed_amount_eur: number;
  amount: number;
  amount_eur: number;
  conversion_rate: number;
}

const AddFunds: React.FC = () => {
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [baseCurrencyId, setBaseCurrencyId] = useState<number>(0);
  const [editableDistribution, setEditableDistribution] = useState<Record<number, DistributionItem>>({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedDistributions, setExpandedDistributions] = useState<Set<number>>(new Set());
  const [showAllDistributions, setShowAllDistributions] = useState(false);
  
  // Estados para movimento manual
  const [activeTab, setActiveTab] = useState<'distribution' | 'manual'>('distribution');
  const [manualProductId, setManualProductId] = useState<number>(0);
  const [manualAmount, setManualAmount] = useState<number>(0);
  const [manualInsertionDate, setManualInsertionDate] = useState<string>(new Date().toISOString().slice(0, 16));
  const [manualNotes, setManualNotes] = useState<string>('');
  const [isSavingManual, setIsSavingManual] = useState(false);
  
  // Estado para data de inserção na distribuição
  const [distributionInsertionDate, setDistributionInsertionDate] = useState<string>(new Date().toISOString().slice(0, 16));

  const queryClient = useQueryClient();

  // Queries
  const { data: currencies } = useQuery('currencies', getCurrencies);
  const { data: products } = useQuery('products', getInvestmentProducts);
  const { data: capitalIncreases, isLoading: isLoadingCapitalIncreases } = useQuery(
    'capital-increases',
    getCapitalIncreases
  );

  // Mutations
  const createCapitalIncreaseMutation = useMutation(createCapitalIncrease);
  const deleteCapitalIncreaseMutation = useMutation(deleteCapitalIncrease);

  const handleCalculateDistribution = async () => {
    if (!totalAmount || !baseCurrencyId) {
      alert('Por favor, preencha o montante total e selecione a moeda base');
      return;
    }

    setIsCalculating(true);
    try {
      const result = await calculateDistribution({
        total_amount: totalAmount,
        base_currency_id: baseCurrencyId
      });

      // Arredondar valores vindos do backend
      const roundedResult: Record<number, DistributionItem> = {};
      Object.entries(result).forEach(([key, item]) => {
        roundedResult[parseInt(key)] = {
          ...item,
          amount: Math.round(item.amount * 100) / 100,
          amount_eur: Math.round(item.amount_eur * 100) / 100,
          current_value_eur: Math.round(item.current_value_eur * 100) / 100,
          target_value_eur: Math.round(item.target_value_eur * 100) / 100,
          needed_amount_eur: Math.round(item.needed_amount_eur * 100) / 100,
          conversion_rate: Math.round(item.conversion_rate * 10000) / 10000 // 4 casas decimais para taxa
        };
      });
      
      setEditableDistribution(roundedResult);
    } catch (error) {
      console.error('Erro ao calcular distribuição:', error);
      alert('Erro ao calcular distribuição');
    } finally {
      setIsCalculating(false);
    }
  };


  const handleAmountChange = (productId: number, newAmount: number) => {
    setEditableDistribution(prev => {
      const roundedAmount = Math.round(newAmount * 100) / 100; // Arredondar a 2 casas decimais
      const roundedAmountEur = Math.round(roundedAmount * prev[productId].conversion_rate * 100) / 100;
      
      return {
        ...prev,
        [productId]: {
          ...prev[productId],
          amount: roundedAmount,
          amount_eur: roundedAmountEur
        }
      };
    });
  };

  const handleSave = async () => {
    if (Object.keys(editableDistribution).length === 0) {
      alert('Por favor, calcule a distribuição primeiro');
      return;
    }

    setIsSaving(true);
    try {
      // Preparar distribuições customizadas
      const distributions: Record<number, any> = {};
      Object.entries(editableDistribution).forEach(([productId, item]) => {
        distributions[parseInt(productId)] = {
          product_id: item.product_id,
          amount: item.amount, // Valor na moeda do produto
          amount_eur: item.amount_eur // Valor convertido para EUR (para cálculo do total)
        };
      });

      // Criar capital increase com distribuições customizadas
      const capitalIncreaseData = {
        base_currency_id: baseCurrencyId,
        increase_date: new Date(distributionInsertionDate).toISOString(),
        notes: `Distribuição manual - ${Object.keys(groupByPlatform()).join(', ')} - Total real: ${getTotalAllocated().toFixed(2)}€`,
        distributions: distributions
      };

      const capitalIncrease = await createCapitalIncreaseMutation.mutateAsync(capitalIncreaseData);

      // Limpar formulário
      setTotalAmount(0);
      setBaseCurrencyId(0);
      setEditableDistribution({});

      // Invalidar queries para atualizar dados
      queryClient.invalidateQueries('dashboard-overview');
      queryClient.invalidateQueries('capital-increases');

      alert(`Capital increase criado com sucesso! ID: ${capitalIncrease.id}`);
    } catch (error) {
      console.error('Erro ao salvar capital increase:', error);
      alert('Erro ao salvar capital increase');
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    // Mapear códigos de moeda não padrão para códigos ISO 4217 válidos
    const currencyMap: Record<string, string> = {
      'USDT': 'USD', // Tether USD -> USD
      'BTC': 'USD',  // Bitcoin -> USD (para formatação)
      'ETH': 'USD',  // Ethereum -> USD (para formatação)
      'EUR': 'EUR',
      'USD': 'USD',
      'GBP': 'GBP',
    };

    const validCurrency = currencyMap[currency] || 'EUR';
    
    try {
      return new Intl.NumberFormat('pt-PT', {
        style: 'currency',
        currency: validCurrency,
      }).format(amount);
    } catch (error) {
      // Fallback se ainda houver erro
      return `${amount.toFixed(2)} ${currency}`;
    }
  };

  const getTotalAllocated = () => {
    const total = Object.values(editableDistribution).reduce((sum, item) => sum + item.amount_eur, 0);
    return Math.round(total * 100) / 100; // Arredondar a 2 casas decimais
  };

  const getRemainingAmount = () => {
    const remaining = totalAmount - getTotalAllocated();
    return Math.round(remaining * 100) / 100; // Arredondar a 2 casas decimais
  };

  const getBaseCurrencyCode = () => {
    return currencies?.find(c => c.id === baseCurrencyId)?.code || 'EUR';
  };

  const formatConversionRate = (rate: any): string => {
    const numRate = Number(rate);
    return isNaN(numRate) ? 'N/A' : numRate.toFixed(4);
  };

  const groupByPlatform = () => {
    const grouped: Record<string, DistributionItem[]> = {};
    if (editableDistribution && typeof editableDistribution === 'object') {
      Object.values(editableDistribution).forEach(item => {
        if (item && item.platform_name) {
          if (!grouped[item.platform_name]) {
            grouped[item.platform_name] = [];
          }
          grouped[item.platform_name].push(item);
        }
      });
    }
    return grouped;
  };

  const groupByAreaInPlatform = (platformItems: DistributionItem[]) => {
    const grouped: Record<string, DistributionItem[]> = {};
    if (Array.isArray(platformItems)) {
      platformItems.forEach(item => {
        if (item && item.area_name) {
          if (!grouped[item.area_name]) {
            grouped[item.area_name] = [];
          }
          grouped[item.area_name].push(item);
        }
      });
    }
    return grouped;
  };

  const getPlatformTotal = (items: DistributionItem[]) => {
    if (!Array.isArray(items)) return 0;
    const total = items.reduce((sum, item) => sum + (item?.amount_eur || 0), 0);
    return Math.round(total * 100) / 100; // Arredondar a 2 casas decimais
  };

  const getAreaTotal = (items: DistributionItem[]) => {
    if (!Array.isArray(items)) return 0;
    const total = items.reduce((sum, item) => sum + (item?.amount_eur || 0), 0);
    return Math.round(total * 100) / 100; // Arredondar a 2 casas decimais
  };

  const getPlatformPercentage = (items: DistributionItem[]) => {
    const platformTotal = getPlatformTotal(items);
    const percentage = totalAmount > 0 ? (platformTotal / totalAmount) * 100 : 0;
    return Math.round(percentage * 100) / 100; // Arredondar a 2 casas decimais
  };

  const getAreaPercentage = (items: DistributionItem[]) => {
    const areaTotal = getAreaTotal(items);
    const percentage = totalAmount > 0 ? (areaTotal / totalAmount) * 100 : 0;
    return Math.round(percentage * 100) / 100; // Arredondar a 2 casas decimais
  };

  const toggleDistributionExpansion = (id: number) => {
    setExpandedDistributions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleDeleteDistribution = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja eliminar esta distribuição? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      await deleteCapitalIncreaseMutation.mutateAsync(id);
      queryClient.invalidateQueries('capital-increases');
      queryClient.invalidateQueries('dashboard-overview');
      alert('Distribuição eliminada com sucesso!');
    } catch (error) {
      console.error('Erro ao eliminar distribuição:', error);
      alert('Erro ao eliminar distribuição');
    }
  };

  const handleSaveManualMovement = async () => {
    if (!manualProductId || !manualAmount) {
      alert('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    setIsSavingManual(true);
    try {
      // Obter o produto selecionado para obter a moeda
      const selectedProduct = products?.find(p => p.id === manualProductId);
      if (!selectedProduct) {
        alert('Produto não encontrado');
        return;
      }

      // Criar capital increase com distribuição para o produto específico
      const capitalIncreaseData = {
        base_currency_id: selectedProduct.currency_id,
        increase_date: new Date(manualInsertionDate).toISOString(),
        notes: manualNotes || `Movimento manual - ${selectedProduct.name}`,
        distributions: {
          [manualProductId]: {
            product_id: manualProductId,
            amount: manualAmount,
            amount_eur: manualAmount // Assumindo que o valor já está em EUR ou será convertido pelo backend
          }
        }
      };

      const result = await createCapitalIncreaseMutation.mutateAsync(capitalIncreaseData);

      // Limpar formulário
      setManualProductId(0);
      setManualAmount(0);
      setManualInsertionDate(new Date().toISOString().slice(0, 16));
      setManualNotes('');

      // Invalidar queries para atualizar dados
      queryClient.invalidateQueries('dashboard-overview');
      queryClient.invalidateQueries('capital-increases');

      alert(`Movimento manual criado com sucesso! ID: ${result.id}`);
    } catch (error) {
      console.error('Erro ao salvar movimento manual:', error);
      alert('Erro ao salvar movimento manual');
    } finally {
      setIsSavingManual(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Adicionar Fundos</h1>
        <p className="text-gray-600">Adicione novos fundos ao seu portefólio com distribuição automática ou movimento manual</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('distribution')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'distribution'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Calculator className="h-4 w-4 inline mr-2" />
            Distribuição Automática
          </button>
          <button
            onClick={() => setActiveTab('manual')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'manual'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Target className="h-4 w-4 inline mr-2" />
            Movimento Manual
          </button>
        </nav>
      </div>

      {/* Distribuição Automática */}
      {activeTab === 'distribution' && (
        <>
          {/* Input Section */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Configuração Inicial</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montante Total
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={totalAmount || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setTotalAmount(Math.round(value * 100) / 100); // Arredondar a 2 casas decimais
                  }}
                  className="input"
                  placeholder="Ex: 1000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Moeda Base
                </label>
                <select
                  value={baseCurrencyId || ''}
                  onChange={(e) => setBaseCurrencyId(parseInt(e.target.value) || 0)}
                  className="input"
                >
                  <option value="">Selecionar moeda</option>
                  {currencies?.map((currency) => (
                    <option key={currency.id} value={currency.id}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Inserção
                </label>
                <input
                  type="datetime-local"
                  value={distributionInsertionDate}
                  onChange={(e) => setDistributionInsertionDate(e.target.value)}
                  className="input"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleCalculateDistribution}
                  disabled={!totalAmount || !baseCurrencyId || isCalculating}
                  className="btn btn-primary w-full"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  {isCalculating ? 'Calculando...' : 'Calcular Distribuição'}
                </button>
              </div>
            </div>
          </div>

      {/* Distribution Section */}
      {Object.keys(editableDistribution).length > 0 && (
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Distribuição por Plataforma</h3>
            <div className="text-sm text-gray-600">
              <div>Total Alocado: {formatCurrency(getTotalAllocated(), getBaseCurrencyCode())}</div>
              <div className={getRemainingAmount() < 0 ? 'text-red-600' : 'text-green-600'}>
                Restante: {formatCurrency(getRemainingAmount(), getBaseCurrencyCode())}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {Object.entries(groupByPlatform() || {}).map(([platformName, platformItems]) => (
              <div key={platformName} className="border border-gray-200 rounded-lg p-4">
                {/* Platform Header */}
                <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-100">
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">{platformName}</h4>
                    <div className="text-sm text-gray-600">
                      {getPlatformPercentage(platformItems).toFixed(2)}% • {formatCurrency(getPlatformTotal(platformItems), getBaseCurrencyCode())}
                    </div>
                  </div>
                </div>

                {/* Areas in Platform */}
                <div className="space-y-4">
                  {Object.entries(groupByAreaInPlatform(platformItems || [])).map(([areaName, areaItems]) => (
                    <div key={areaName} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                      {/* Area Header */}
                      <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
                        <div>
                          <h5 className="font-medium text-gray-800">{areaName}</h5>
                          <div className="text-sm text-gray-600">
                            {getAreaPercentage(areaItems).toFixed(2)}% • {formatCurrency(getAreaTotal(areaItems), getBaseCurrencyCode())}
                          </div>
                        </div>
                      </div>

                      {/* Products in Area */}
                      <div className="space-y-3">
                        {(areaItems || []).map((item) => (
                          <div key={item.product_id} className="p-3 bg-white rounded-lg border border-gray-200">
                            <div className="flex items-center space-x-4">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{item.product_name}</div>
                                <div className="text-sm text-gray-600">
                                  {item.target_percentage.toFixed(2)}% • {item.currency_code}
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <div className="text-right">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={item.amount}
                                    onChange={(e) => handleAmountChange(item.product_id, parseFloat(e.target.value) || 0)}
                                    className="input w-32 text-right"
                                  />
                                </div>
                                
                                <div className="text-sm text-gray-600 min-w-[120px]">
                                  <div className="font-medium">
                                    {formatCurrency(item.amount, item.currency_code)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    = {formatCurrency(item.amount_eur, getBaseCurrencyCode())}
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Rebalancing Info */}
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <div className="grid grid-cols-3 gap-4 text-xs">
                                <div>
                                  <div className="text-gray-500">Atual:</div>
                                  <div className="font-medium">{formatCurrency(item.current_value_eur, getBaseCurrencyCode())}</div>
                                </div>
                                <div>
                                  <div className="text-gray-500">Alvo:</div>
                                  <div className="font-medium">{formatCurrency(item.target_value_eur, getBaseCurrencyCode())}</div>
                                </div>
                                <div>
                                  <div className="text-gray-500">Necessário:</div>
                                  <div className={`font-medium ${item.needed_amount_eur > 0 ? 'text-blue-600' : 'text-green-600'}`}>
                                    {formatCurrency(item.needed_amount_eur, getBaseCurrencyCode())}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => {
                setEditableDistribution({});
              }}
              className="btn btn-secondary"
            >
              <X className="h-4 w-4 mr-2" />
              Limpar
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn btn-primary"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'A Guardar...' : 'Guardar Investimentos'}
            </button>
          </div>
        </div>
      )}

      {/* Summary */}
      {Object.keys(editableDistribution).length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Resumo da Distribuição</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Distribuição por Plataforma</h4>
              <div className="space-y-2">
                {Object.entries(groupByPlatform() || {}).map(([platformName, items]) => {
                  const platformTotal = getPlatformTotal(items);
                  const platformPercentage = getPlatformPercentage(items);
                  return (
                    <div key={platformName} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div>
                        <div className="font-medium text-sm">{platformName}</div>
                        <div className="text-xs text-gray-500">{items.length} produto(s)</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">
                          {formatCurrency(platformTotal, getBaseCurrencyCode())}
                        </div>
                        <div className="text-xs text-gray-500">
                          {platformPercentage.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Estatísticas Gerais</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span>Total de Plataformas:</span>
                  <span className="font-medium">{Object.keys(groupByPlatform()).length}</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span>Total de Áreas:</span>
                  <span className="font-medium">{
                    Object.values(groupByPlatform()).reduce((total, platformItems) => {
                      return total + Object.keys(groupByAreaInPlatform(platformItems)).length;
                    }, 0)
                  }</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span>Total de Produtos:</span>
                  <span className="font-medium">{Object.keys(editableDistribution).length}</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span>Produtos com Valor:</span>
                  <span className="font-medium">{Object.values(editableDistribution).filter(item => item.amount > 0).length}</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span>Valor Total ({getBaseCurrencyCode()}):</span>
                  <span className="font-medium">{formatCurrency(getTotalAllocated(), getBaseCurrencyCode())}</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span>Restante:</span>
                  <span className={`font-medium ${getRemainingAmount() < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(getRemainingAmount(), getBaseCurrencyCode())}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Movimento Manual */}
      {activeTab === 'manual' && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Movimento Manual</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Produto *
                </label>
                <select
                  value={manualProductId || ''}
                  onChange={(e) => setManualProductId(parseInt(e.target.value) || 0)}
                  className="input"
                >
                  <option value="">Selecionar produto</option>
                  {products?.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} ({product.area_name}) - {product.currency_code}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montante *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={manualAmount || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setManualAmount(Math.round(value * 100) / 100);
                  }}
                  className="input"
                  placeholder="Ex: 1000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Inserção *
                </label>
                <input
                  type="datetime-local"
                  value={manualInsertionDate}
                  onChange={(e) => setManualInsertionDate(e.target.value)}
                  className="input"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={manualNotes}
                  onChange={(e) => setManualNotes(e.target.value)}
                  className="input"
                  rows={4}
                  placeholder="Notas opcionais sobre este movimento..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setManualProductId(0);
                    setManualAmount(0);
                    setManualInsertionDate(new Date().toISOString().slice(0, 16));
                    setManualNotes('');
                  }}
                  className="btn btn-secondary"
                >
                  <X className="h-4 w-4 mr-2" />
                  Limpar
                </button>
                <button
                  onClick={handleSaveManualMovement}
                  disabled={isSavingManual}
                  className="btn btn-primary"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSavingManual ? 'A Guardar...' : 'Guardar Movimento'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Fundos Adicionados */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Histórico de Movimentos</h3>
          {capitalIncreases && Array.isArray(capitalIncreases) && capitalIncreases.length > 5 && (
            <button
              onClick={() => setShowAllDistributions(!showAllDistributions)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showAllDistributions ? 'Mostrar menos' : `Mostrar todos (${capitalIncreases.length})`}
            </button>
          )}
        </div>
        
        {isLoadingCapitalIncreases ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : capitalIncreases && Array.isArray(capitalIncreases) && capitalIncreases.length > 0 ? (
          <div className="space-y-2">
            {(showAllDistributions ? capitalIncreases : capitalIncreases.slice(0, 5)).map((capitalIncrease) => (
              <div key={capitalIncrease.id} className="border border-gray-200 rounded-lg">
                {/* Header Condensado */}
                <div className="flex justify-between items-center p-3">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => toggleDistributionExpansion(capitalIncrease.id)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {expandedDistributions.has(capitalIncrease.id) ? 
                        <ChevronDown className="h-4 w-4" /> : 
                        <ChevronRight className="h-4 w-4" />
                      }
                    </button>
                    <div>
                      <div className="font-medium text-gray-900 text-sm">
                        #{capitalIncrease.id} • {new Date(capitalIncrease.increase_date).toLocaleDateString('pt-PT')}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatCurrency(capitalIncrease.total_amount, capitalIncrease.base_currency_code)}
                        {capitalIncrease.distributions && capitalIncrease.distributions.length > 0 && (
                          <span> • {capitalIncrease.distributions.length} produto(s)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className="badge badge-secondary text-xs">
                      {capitalIncrease.base_currency_code}
                    </span>
                    <button
                      onClick={() => handleDeleteDistribution(capitalIncrease.id)}
                      disabled={deleteCapitalIncreaseMutation.isLoading}
                      className="text-red-500 hover:text-red-700 transition-colors p-1"
                      title="Eliminar distribuição"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Detalhes Expandidos */}
                {expandedDistributions.has(capitalIncrease.id) && (
                  <div className="border-t border-gray-100 p-3 bg-gray-50">
                    {capitalIncrease.distributions && capitalIncrease.distributions.length > 0 ? (
                      <div className="space-y-2">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Distribuições:</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {(capitalIncrease.distributions || []).map((distribution) => (
                            <div key={distribution.id} className="p-2 bg-white rounded text-sm border border-gray-200">
                              <div className="font-medium text-gray-900">{distribution.product_name}</div>
                              <div className="text-gray-600 text-xs">
                                {distribution.area_name} • {formatCurrency(distribution.allocated_amount, distribution.currency_code)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">Nenhuma distribuição encontrada</div>
                    )}

                    {/* Notas */}
                    {capitalIncrease.notes && (
                      <div className="mt-3 pt-2 border-t border-gray-200">
                        <div className="text-xs text-gray-500">
                          <strong>Notas:</strong> {capitalIncrease.notes}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>Nenhum fundo foi adicionado ainda.</p>
            <p className="text-sm">Use o formulário acima para adicionar o primeiro fundo.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddFunds;